<script setup lang="ts">
import {useExtensionStore} from "@/stores/extension.store";
import {SubscriptionPlan} from "@/subscription-plan/interfaces";
import {computed} from "vue";
import {storeToRefs} from "pinia";
import Extension from "@/core/interfaces/extension/Extension";
import EmbedVideoModalButton from "@/video/components/EmbedVideoModalButton.vue";
import {Play} from "@lucide/vue";
import {ExtensionGrid, ExtensionGridTitle} from "@/extension/components/extension-grid";

const {enabledExtensionsBySubscriptionPlan} = useExtensionStore();
const {extensions} = storeToRefs(useExtensionStore());

defineEmits<{
    (e: 'extensionClick', extension: Extension): void;
}>();
const props = defineProps<{
    /** The subscription plan to show. */
    plan: SubscriptionPlan,
    /** Whether only enabled extensions should be displayed. */
    enabled: boolean;
    /** Whether a plus icon should be displayed at the end of the list. */
    appendsIcon: boolean;
    /** Whether the toggle to enable/disable extensions should be displayed. */
    enableToggle?: boolean;
}>();

const displayedExtensions = computed(() => {
    if (props.enabled) {
        return enabledExtensionsBySubscriptionPlan(props.plan.id) ?? [];
    }

    return extensions.value?.filter(extension => {
        return extension.subscription_plan_id === props.plan.id;
    }) ?? [];
});

</script>

<template>
  <ExtensionGrid
    :appends-icon="appendsIcon"
    :title="plan.name"
    :extensions="displayedExtensions"
    :enable-toggle="enableToggle"
    @extension-click="(extension) => $emit('extensionClick', extension)"
  >
    <template #header>
      <div class="flex items-center">
        <img v-if="plan.logo" :src="plan.logo.url" :alt="plan.name" class="w-8 h-8 mr-2 ">
        <ExtensionGridTitle class="flex-1">
          {{ plan.name }} <span v-if="plan.teaser" class="text-primary text-sm font-normal">
            - {{ plan.teaser }}
          </span>
        </ExtensionGridTitle>
        <div v-if="plan.video_url">
          <EmbedVideoModalButton :embed-video-url="plan.video_url" :title="plan.name">
            <div class="flex items-center text-sm text-primary gap-1 bg-[#DDDDF5] hover:bg-lightPurple-lightest px-2 py-1 rounded-lg">
              <Play class="fill-red-500 text-red-500 shrink-0 w-5" />
              <p>
                Voir le tuto
              </p>
            </div>
          </EmbedVideoModalButton>
        </div>
      </div>
    </template>
  </ExtensionGrid>
</template>
